/** @flow */
import * as React from 'react';
import { Redirect, Route } from 'react-router-dom';
import { connect } from 'react-redux';

import Radio from 'common/domain/radio';
import User from 'common/domain/user';
import SplashScreen from 'common/modules/ui/components/SplashScreen';
import { isDevFlagEnabled } from 'common/components/DevFlag';
import FeaturePermission from 'common/components/FeaturePermission';
import type { AppState } from 'statsradio/types/State';

import { getCurrentRadio } from './session/selectors/sessionSelectors';
import AuthorisedComponent from '../authorisation/AuthorisedComponent';
import DefaultRouteRedirect from '../authorisation/DefaultRouteRedirect';
import type { Permission } from '../authorisation/Permissions';

type Props<T> = T & {
  component: React.Element<any>,
  isAuthenticated: boolean,
  user: User,
  permission?: Permission,
  featurePermission: FeaturePermission,
  isSwitchingRadio: boolean,
  location: any,
  hasSelectedRadio: boolean,
  allowNotConfiguredRadio: boolean,
  radio: Radio,
  hideForSmallMarket: boolean
};

type permittedComponentProps = {
  component: any,
  permission?: Permission,
  featurePermission?: FeaturePermission
};

const PermittedComponent = ({ component, permission, featurePermission, ...rest }: permittedComponentProps) => {
  const MyComponent = component;

  if (!permission && !featurePermission) {
    return (<MyComponent {...rest} />);
  }

  return (
    <AuthorisedComponent
      permissions={permission}
      featurePermissions={featurePermission}
      FailureComponent={() => <DefaultRouteRedirect />}
    >
      <MyComponent {...rest} />
    </AuthorisedComponent>
  );
};

const radioIsNotConfigured = (radio: Radio, allow: boolean) => {
  if (allow) {
    return false;
  }

  return !radio.configurationIs('DONE');
};

function PrivateRoute<T>(privateRouteProps: Props<T>) {
  const {
    allowNotConfiguredRadio, radio, component, permission, featurePermission,
    isAuthenticated, hasSelectedRadio, isSwitchingRadio, location, hideForSmallMarket, ...rest
  } = privateRouteProps;

  if (!isAuthenticated) {
    return (
      <Route {...rest} render={(props: Object) => (
        <Redirect to={{ pathname: '/login', state: { from: props.location } }} />
      )}
      />
    );
  }

  if (!hasSelectedRadio && location.pathname !== '/select-radio') {
    return <Redirect to={{ pathname: '/select-radio', state: { from: location } }} />;
  }

  if (hasSelectedRadio && radio.isInSmallMarket() && hideForSmallMarket) {
    return <DefaultRouteRedirect />;
  }

  if (isDevFlagEnabled('RADIO_CONFIGURATION')) {
    if (radioIsNotConfigured(radio, allowNotConfiguredRadio)) {
      return <Redirect to={{ pathname: '/configure-radio', state: { from: location } }} />;
    }
  }

  return (
    <div>
      {isSwitchingRadio && <SplashScreen />}
      <Route {...rest} location={location} render={(props: Object) => (       // $FlowFixMe
        <PermittedComponent
          permission={permission}
          featurePermission={featurePermission}
          component={component} {...props}
        />
      )}
      />
    </div>
  );
}


export default connect((state: AppState) => ({
  radio: getCurrentRadio(state),
  isAuthenticated: state.session.isAuthenticated,
  isSwitchingRadio: state.session.isSwitchingRadio,
  hasSelectedRadio: Boolean(state.session.radioUser),
}))(PrivateRoute);



// WEBPACK FOOTER //
// ./src/statsradio/modules/authentication/PrivateRoute.js